// reCAPTCHA v3: site key injected at build via Vite env (public key, safe to expose).
declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const RECAPTCHA_SITE_KEY: string =
  (import.meta as { env?: Record<string, string> }).env?.VITE_RECAPTCHA_SITE_KEY ?? "";

export async function getRecaptchaToken(action: string): Promise<string> {
  const g = window.grecaptcha;
  if (!g || !RECAPTCHA_SITE_KEY) return ""; // not configured -> server decides if required
  await new Promise<void>((resolve) => g.ready(resolve));
  return g.execute(RECAPTCHA_SITE_KEY, { action });
}

export type NicheProfile = {
  name: string;
  displayName: string;
  targetBuyer: string;
  offerDescription: string;
  subreddits: string[];
  keywords: string[];
  modelPath?: string;
  transformerThreshold: number;
  evalDataPath?: string;
};

export type NicheResponse = {
  active: string;
  profiles: NicheProfile[];
};

export type Lead = {
  id: number;
  url: string;
  platform: string;
  subreddit?: string;
  niche?: string;
  title?: string;
  content?: string;
  author?: string;
  sourceKind?: string;
  commentCount?: number;
  intentScore: number;
  leadGrade?: string;
  reasonFamily?: string;
  buyerStage?: string;
  triggerEvent?: string;
  intentSignals?: string;
  intentLabel: string;
  intentType?: string;
  intentFit?: string;
  verificationConfidence?: number;
  purchaseLikelihood?: number;
  urgency: string;
  urgencyScore: number;
  evidenceSnippet?: string;
  evidenceSummary?: string;
  verificationStatus: string;
  sourceStatus?: string;
  riskFlags: string[];
  suggestedAction: string;
  keyword?: string;
  publishedAt?: string;
  postedAge?: string;
  status: string;
  foundAt: string;
  lastSeenAt: string;
  timesSeen: number;
};

export type JobState = {
  isRunning: boolean;
  isComplete: boolean;
  alreadySearched?: boolean;
  contactRequired?: boolean;
  message?: string;
  keyword?: string;
  firstSearchedAt?: string;
  lastError?: string;
  logs?: string[];
  step?: string;
  progress?: {
    statusLine?: string;
  };
  [key: string]: unknown;
};

export type AuthState = {
  isAuthenticated: boolean;
  id?: string;
  email?: string;
  displayName?: string;
  apiAuthRequired: boolean;
  googleEnabled: boolean;
  jwtEnabled: boolean;
  isOperationsAdmin: boolean;
  activeTenant?: TenantState;
  tenants: TenantState[];
};

export type OperatorHealthEvent = {
  timestamp: string;
  severity: string;
  service: string;
  action: string;
  message: string;
  source: string;
};

export type OperatorEventSnapshot = {
  lastCheckedAt?: string;
  events: OperatorHealthEvent[];
};

export type BillingPlan = {
  name: "starter" | "pro" | "agency" | string;
  displayName: string;
  configured: boolean;
};

export type BillingState = {
  stripeEnabled: boolean;
  checkoutEnabled: boolean;
  portalEnabled: boolean;
  hasAccess: boolean;
  plan: string;
  tenantStatus: string;
  subscriptionStatus: string;
  subscriptionCurrentPeriodEnd?: string;
  trialEndsAt?: string;
  stripePriceId?: string;
  plans: BillingPlan[];
  message?: string;
};

export type TenantState = {
  id: string;
  name: string;
  slug: string;
  role: string;
  isDefault: boolean;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    credentials: "include",
    ...init
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(errorMessage(text, response.status));
  }

  return response.json() as Promise<T>;
}

function errorMessage(text: string, status: number) {
  if (!text) return `HTTP ${status}`;

  try {
    const parsed = JSON.parse(text) as { detail?: string; title?: string; error?: string; message?: string };
    return parsed.detail || parsed.error || parsed.message || parsed.title || text;
  } catch {
    return text;
  }
}

export const api = {
  health: () => request<{ status: string; timeUtc: string }>("/api/health"),
  operatorHealthEvents: (limit = 40) =>
    request<OperatorEventSnapshot>(`/api/operations/health-events?limit=${limit}`),
  currentUser: () => request<AuthState>("/api/auth/me"),
  tenants: () => request<{ active: TenantState; memberships: TenantState[] }>("/api/tenants"),
  googleLoginUrl: (returnUrl = "/") => `/api/auth/google?returnUrl=${encodeURIComponent(returnUrl)}`,
  logout: () => request<{ status: string }>("/api/auth/logout", { method: "POST" }),
  jwtToken: () =>
    request<{ accessToken: string; tokenType: string; expiresAt: string; issuer: string; audience: string }>(
      "/api/auth/token",
      { method: "POST" }
    ),
  modelHealth: () => request<Record<string, unknown>>("/api/model/health"),
  billing: () => request<BillingState>("/api/billing"),
  startCheckout: (plan: string) =>
    request<{ url: string }>("/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ plan })
    }),
  billingPortal: () => request<{ url: string }>("/api/billing/portal", { method: "POST" }),
  contactSales: async (body: { message: string; subject?: string; keyword?: string; source?: string }) => {
    const recaptchaToken = await getRecaptchaToken("contact");
    return request<{ status: string; inquiryId: number; forwarded: boolean }>("/api/contact/sales", {
      method: "POST",
      body: JSON.stringify({ ...body, recaptchaToken })
    });
  },
  niches: () => request<NicheResponse>("/api/niches"),
  setActiveNiche: (name: string) =>
    request<{ active: string }>("/api/niches/active", {
      method: "PUT",
      body: JSON.stringify({ name })
    }),
  leads: (params: { niche?: string; keyword?: string; status?: string; grade?: string; seenAfter?: string; sort?: string; limit?: number }) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") search.set(key, String(value));
    });
    return request<Lead[]>(`/api/leads?${search}`);
  },
  leadsCsvUrl: (params: { niche?: string; status?: string; grade?: string; seenAfter?: string; sort?: string; limit?: number }) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") search.set(key, String(value));
    });
    return `/api/leads/export.csv?${search}`;
  },
  codexReviewBatchUrl: (params: { niche?: string; status?: string; grade?: string; seenAfter?: string; sort?: string; limit?: number }) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") search.set(key, String(value));
    });
    return `/api/leads/codex-review.jsonl?${search}`;
  },
  updateLead: (
    id: number,
    body: { status: string; label?: "buyer" | "not_buyer"; reason?: string; source?: string }
  ) =>
    request<{ lead: Lead; feedback?: unknown }>(`/api/leads/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
  setLeadPreference: (id: number, verdict: "up" | "down") =>
    request<{ recorded: boolean; verdict: string }>(`/api/leads/${id}/preference`, {
      method: "POST",
      body: JSON.stringify({ verdict })
    }),
  liveFetch: () => request<JobState>("/api/jobs/live-fetch"),
  startLiveFetch: (body: {
    daysBack: number;
    sources?: string[];
    keyword?: string;
    startDate?: string;
    endDate?: string;
    includeProfileSeeds?: boolean;
    contextTerms?: string[];
    searchPhrases?: string[];
  }) =>
    request<JobState>("/api/jobs/live-fetch/start", {
      method: "POST",
      body: JSON.stringify({
        daysBack: body.daysBack,
        sources: body.sources?.length ? body.sources : undefined,
        keyword: body.keyword,
        startDate: body.startDate,
        endDate: body.endDate,
        includeProfileSeeds: body.includeProfileSeeds,
        contextTerms: body.contextTerms?.length ? body.contextTerms : undefined,
        searchPhrases: body.searchPhrases?.length ? body.searchPhrases : undefined
      })
    }),
  stopLiveFetch: () => request<JobState>("/api/jobs/live-fetch/stop", { method: "POST" }),
  sourceDiscovery: () => request<JobState>("/api/jobs/source-discovery"),
  startSourceDiscovery: (
    keyword: string,
    days: number,
    top = 25,
    sample = 60,
    startDate?: string,
    endDate?: string,
    includeProfileSeeds?: boolean
  ) =>
    request<JobState>("/api/jobs/source-discovery/start", {
      method: "POST",
      body: JSON.stringify({ keyword, days, top, sample, startDate, endDate, includeProfileSeeds })
    }),
  stopSourceDiscovery: () => request<JobState>("/api/jobs/source-discovery/stop", { method: "POST" }),
  scoring: () => request<JobState>("/api/jobs/scoring"),
  startScoring: () =>
    request<JobState>("/api/jobs/scoring/start", {
      method: "POST",
      body: JSON.stringify({ resume: true })
    }),
  training: () => request<JobState>("/api/jobs/training"),
  startTraining: (epochs: number) =>
    request<JobState>("/api/jobs/training/start", {
      method: "POST",
      body: JSON.stringify({ epochs, fresh: false })
    }),
  stopTraining: () => request<JobState>("/api/jobs/training/stop", { method: "POST" })
};
